import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion'; 

// --- Icon Components ---

const BackArrow = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="19" y1="12" x2="5" y2="12"></line>
        <polyline points="12 19 5 12 12 5"></polyline>
    </svg>
);

const GoogleImage = () => (
    // UPDATED: Using the provided Brandfetch SVG URL
    <img 
        src="https://cdn.brandfetch.io/id6O2oGzv-/theme/dark/symbol.svg?c=1dxbfHSJFAPEGdCLU4o5B" 
        alt="Google icon" 
        style={{ marginRight: '8px', width: '18px', height: '18px' }} 
    />
);

const VisibilityIcon = ({ isVisible }) => {
    const IconComponent = isVisible ? (
        // Eye (Visible) Icon
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
            <circle cx="12" cy="12" r="3" />
        </svg>
    ) : (
        // Eye-Off (Hidden) Icon
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-3.21 0-6.24-1.92-8.38-5.38a1 1 0 0 1 0-1.24c1.16-1.98 2.65-3.5 4.38-4.57" />
            <path d="M2.5 2.5 21.5 21.5" />
            <path d="M12 7.77a3 3 0 0 1 3.44 3.44" />
            <path d="M7 12s3 7 10 7 10-7 10-7-3-7-10-7" />
        </svg>
    );
    return IconComponent;
};


const AuthCard = ({ type = 'login' }) => {
    
    const navigate = useNavigate();
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);

    const isLogin = type === 'login';
    
    // Conditional text based on type
    const primaryButtonText = isLogin ? 'Login' : 'Create Account';
    const dividerText = isLogin ? 'or Login with Email' : 'or Sign Up with Email';
    
    const handleSubmit = (event) => {
        event.preventDefault();
        console.log(`${isLogin ? 'Login' : 'Signup'} attempt.`);
    };
    
    const handleGoogleAuth = () => {
        console.log('Google Auth clicked.');
    };
    
    const handleTogglePassword = () => {
        setShowPassword(!showPassword);
    };

    // --- FRAMER MOTION VARIANTS ---
    const cardVariants = {
        hidden: { scale: 0.8, opacity: 0 },
        visible: { 
            scale: 1, 
            opacity: 1, 
            transition: { type: "spring", stiffness: 150, damping: 18 }
        }
    };


    return (
        <div className="auth-page-container">
            <motion.div 
                className="auth-card"
                variants={cardVariants}
                initial="hidden"
                animate="visible"
            >
                
                {/* Back Arrow & Title/Greeting Group */}
                <div className="auth-card-header">
                    <motion.button 
                        onClick={() => navigate(-1)} 
                        className="auth-back-arrow"
                        aria-label="go back"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                    >
                        <BackArrow />
                    </motion.button>
                    
                    {/* Conditional Header Content */}
                    {isLogin ? (
                        <div style={{ paddingLeft: '5px' }}>
                            <p className="auth-card-subtitle" style={{ margin: '0', textAlign: 'left', fontWeight: 'bold' }}>
                                Hi, Welcome back 👋
                            </p>
                            <h2 className="auth-card-title" style={{ fontSize: '34px', marginTop: '0px' }}>
                                Login
                            </h2>
                        </div>
                    ) : (
                        <div style={{ paddingLeft: '5px' }}>
                            <h2 className="auth-card-title">
                                Sign Up
                            </h2>
                            <p className="auth-card-subtitle" style={{ margin: '0', textAlign: 'left' }}>
                                Let's get you set up 🙌
                            </p>
                        </div>
                    )}
                </div>
                
                {/* Google Button */}
                <motion.button 
                    className="auth-button auth-google-button"
                    onClick={handleGoogleAuth}
                    type="button"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                >
                    <GoogleImage />
                    {isLogin ? 'Login with Google' : 'Sign Up with Google'}
                </motion.button>

                {/* OR Separator */}
                <div className="auth-divider-container">
                    <hr className="auth-divider-line" />
                    <span className="auth-divider-text">{dividerText}</span>
                    <hr className="auth-divider-line" />
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="auth-form">
                    
                    {/* Full Name Input (Signup Only) */}
                    {!isLogin && (
                        <input
                            className="auth-input-field"
                            type="text"
                            placeholder="Full Name"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            required
                        />
                    )}

                    {/* Email Input */}
                    <input
                        className="auth-input-field"
                        type="email"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />

                    {/* Password Input (The fixed element) */}
                    <div className="auth-password-container" style={{ position: 'relative' }}>
                        <input
                            className="auth-input-field"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                        <motion.button 
                            type="button"
                            className="auth-toggle-password-button"
                            onClick={handleTogglePassword}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            style={{ 
                                position: 'absolute', 
                                right: '10px', 
                                top: '50%', 
                                transform: 'translateY(-50%)',
                                height: 'auto', 
                                margin: 0, 
                                padding: 0, 
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                zIndex: 10 
                            }}
                        >
                            <VisibilityIcon isVisible={showPassword} />
                        </motion.button>
                    </div>
                    
                    {/* Checkbox/Links Row */}

                    {/* Login Checkbox/Link Row */}
                    {isLogin && (
                        <div className="auth-forgot-password-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        
                            {/* Remember Me Checkbox */}
                            <label style={{ display: 'flex', alignItems: 'center' }}>
                                <input
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                    style={{ marginRight: '8px' }}
                                />
                                <span style={{ fontSize: '0.85rem', color: 'var(--color-gray-medium)' }}>Remember Me</span>
                            </label>
                            
                            {/* Forgot Password Link */}
                            <RouterLink to="/forgot-password" className="auth-forgot-password-link" style={{ fontSize: '0.85rem' }}>
                                Forgot Password?
                            </RouterLink>
                        </div>
                    )}
                    
                    {/* Terms Checkbox (Signup Only) */}
                    {!isLogin && (
                        <label className="auth-terms-row">
                            <input
                                type="checkbox"
                                className="auth-terms-checkbox"
                                checked={agreedToTerms}
                                onChange={(e) => setAgreedToTerms(e.target.checked)}
                                required
                            />
                            I agree to the <RouterLink to="/terms" className="auth-terms-link">Terms</RouterLink> and <RouterLink to="/privacy" className="auth-terms-link">Privacy Policy</RouterLink>
                        </label>
                    )}

                    {/* Main Button */}
                    <motion.button
                        type="submit"
                        className="auth-button auth-primary-button"
                        style={{ marginTop: isLogin ? '5px' : '0px' }}
                        disabled={!isLogin && !agreedToTerms}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        {primaryButtonText}
                    </motion.button>
                </form>

                {/* Switch Link */}
                <p className="auth-switch-link-container">
                    {isLogin ? "Not registered yet?" : "Already have an account?"}{' '}
                    <RouterLink to={isLogin ? "/signup" : "/login"} className="auth-switch-link">
                        {isLogin ? "Create an account ↗" : "Login ↗"}
                    </RouterLink>
                </p>

            </motion.div>
        </div>
    );
};

export default AuthCard;